import React, { ChangeEvent, useMemo, useState } from 'react'
import { CalculatorInput } from '../elements/CalculatorInput'

import { Button, Icon, Radio, RadioGroup } from '../../../../atoms'
import Trustpilot from '../stamp-duty-calculator/mojo-trustpilot'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { LoanToValueChart } from '../compounds/loan-to-value-chart'
import { Dot } from '../../../../atoms/dot'
import { calculateInterestOnlyMortgage, calculateRepaymentMortgage } from './calculation'
import { ltv } from '../../../helpers/calculation'

ChartJS.register(ArcElement, Tooltip, Legend)

const CLICKOUT_LINK =
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3Dpartner%26utm_medium%3Duswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dremortgage_calculator&sig=8e91ac9124cf673dc445714f7581f023930c60c9e27117b6bad9f90589e13913&brand=uswitch'

enum TypeOfMortgage {
  Repayment = 'Repayment',
  InterestOnly = 'InterestOnly',
}

interface RemortgageCalculatorResults {
  currentMonthlyPayment: number
  newMonthlyPayment: number
}

interface CalculateRemortgageArgs {
  mortgageBalance: number
  currentRate: number
  newRate: number
  years: number
  months: number
  typeOfMortgage: TypeOfMortgage
}

function calculateRemortgage({
  mortgageBalance,
  currentRate,
  newRate,
  years,
  months,
  typeOfMortgage,
}: CalculateRemortgageArgs): RemortgageCalculatorResults {
  const calculateInterestOnly = (rate: number) =>
    calculateInterestOnlyMortgage({
      mortgageBalance: mortgageBalance,
      interestRate: rate,
      years,
      months,
    })

  const calculateRepayment = (rate: number) =>
    calculateRepaymentMortgage({
      mortgageBalance: mortgageBalance,
      interestRate: rate,
      years,
      months,
    })

  switch (typeOfMortgage) {
    case TypeOfMortgage.Repayment:
      return {
        currentMonthlyPayment: calculateRepayment(currentRate),
        newMonthlyPayment: calculateRepayment(newRate),
      }
    case TypeOfMortgage.InterestOnly:
      return {
        currentMonthlyPayment: calculateInterestOnly(currentRate).monthlyPayment,
        newMonthlyPayment: calculateInterestOnly(newRate).monthlyPayment,
      }
  }
}

interface ResultsPageProps {
  onRecalculate: () => void
  results: RemortgageCalculatorResults
  deposit: number
  loanValue: number
  ltv: number
  ctaButtonText: string
}

const ResultsPage = ({
  onRecalculate,
  results,
  deposit,
  loanValue,
  ltv,
  ctaButtonText,
}: ResultsPageProps) => (
  <div className='results-panel'>
    <div className='deposit-info'>
      <div className='deposit-info-title'>
        <h3 style={{ fontWeight: 'bold' }}>Your current mortgage payments are:</h3>
        <h1 className='deposit-info-value'>
          £{results.currentMonthlyPayment.toLocaleString('en-GB')}
        </h1>
      </div>
      <LoanToValueChart loanToValue={ltv} deposit={deposit} borrowing={loanValue} />
    </div>
    <div className='deposit-info'>
      <div className='deposit-info-title'>
        <h3 style={{ fontWeight: 'bold' }}>If you remortgage, your monthly payments could be:</h3>
        <h1 className='deposit-info-value'>£{results.newMonthlyPayment.toLocaleString('en-GB')}</h1>
      </div>
    </div>
    <div className='deposit-details'>
      <table>
        <thead>
          <tr>
            <th>Equity</th>
            <th>Loan value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div className='deposit-details-value'>
                <h3>£{deposit.toLocaleString('en-GB')}</h3> <Dot colour='black' />
              </div>
            </td>
            <td>
              <div className='deposit-details-value'>
                <h3>£{loanValue.toLocaleString('en-GB')}</h3> <Dot colour='#AC96DE' />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div style={{ marginTop: '16px' }}>
      <h3>These results are estimated based on the information provided</h3>
    </div>
    <div className='footer'>
      <Button variant='primary' withIcon href={CLICKOUT_LINK}>
        {ctaButtonText}
      </Button>
      <Button variant='secondary' onClick={onRecalculate}>
        Recalculate
      </Button>
    </div>
  </div>
)

interface CalculatorFormProps {
  updateState: (
    field: keyof RemortgageCalculatorState,
  ) => (e: ChangeEvent<HTMLInputElement>) => void
  calculatorState: RemortgageCalculatorState
  onSubmit: () => void
}

const CalculatorForm = ({ updateState, calculatorState, onSubmit }: CalculatorFormProps) => {
  const isSubmittable =
    calculatorState.propertyValue &&
    calculatorState.mortgageDebt &&
    (calculatorState.yearsLeft || calculatorState.monthsLeft) &&
    calculatorState.typeOfMortgage &&
    calculatorState.currentInterestRate &&
    calculatorState.newInterestRate

  return (
    <>
      <h2>Calculate your remortgage</h2>
      <form className='form' onSubmit={onSubmit}>
        <CalculatorInput
          id='remortgage-property-value'
          label='How much is your property currently worth?'
          name='propertyValue'
          type='text'
          format='thousands'
          placeholder={0}
          prefix='£'
          onChange={updateState('propertyValue')}
          value={calculatorState.propertyValue}
        />
        <CalculatorInput
          id='remortgage-mortgage-debt'
          label='How much do you owe on your current mortgage'
          name='mortgageDebt'
          type='text'
          format='thousands'
          placeholder={0}
          prefix='£'
          onChange={updateState('mortgageDebt')}
          value={calculatorState.mortgageDebt}
        />
        <CalculatorInput
          id='remortgage-current-rate'
          label='What is your current interest rate'
          name='currentInterestRate'
          type='percent'
          placeholder={0}
          suffix='%'
          onChange={updateState('currentInterestRate')}
          value={calculatorState.currentInterestRate}
        />
        <CalculatorInput
          id='remortgage-current-rate'
          label='What is your new interest rate'
          name='newInterestRate'
          type='percent'
          placeholder={0}
          suffix='%'
          onChange={updateState('newInterestRate')}
          value={calculatorState.newInterestRate}
        />
        <label htmlFor='remortgage-years-left'>How long is left on your mortgage?</label>
        <div className='horizontal-stack-input'>
          <CalculatorInput
            id='remortgage-years-left'
            name='yearsLeft'
            type='number'
            format='years'
            placeholder='Years'
            onChange={updateState('yearsLeft')}
            value={calculatorState.yearsLeft}
          />
          <CalculatorInput
            id='remortgage-months-left'
            name='monthsLeft'
            type='number'
            format='months'
            placeholder='Months'
            onChange={updateState('monthsLeft')}
            value={calculatorState.monthsLeft}
          />
        </div>
        <label htmlFor='typeOfMortgage'>What type of mortgage are you after?</label>
        <RadioGroup>
          <Radio
            value={TypeOfMortgage.Repayment}
            name='typeOfMortgage'
            currentValue={calculatorState.typeOfMortgage || ''}
            onChange={updateState('typeOfMortgage')}
          >
            Repayment
          </Radio>
          <Radio
            value={TypeOfMortgage.InterestOnly}
            name='typeOfMortgage'
            currentValue={calculatorState.typeOfMortgage || ''}
            onChange={updateState('typeOfMortgage')}
          >
            Interest-only
          </Radio>
        </RadioGroup>
        <div className='footer'>
          <Button disabled={!isSubmittable} withIcon type='submit'>
            Calculate
          </Button>
        </div>
      </form>
    </>
  )
}

interface RemortgageCalculatorState {
  propertyValue: number
  mortgageDebt: number
  yearsLeft: number
  monthsLeft: number
  currentInterestRate: number
  newInterestRate: number
  typeOfMortgage: TypeOfMortgage | null
}

interface RemortgageCalculatorOptions {
  title: string
  subtitle: string
  ctaButtonText: string
}

export const RemortgageCalculator = ({
  title,
  subtitle,
  ctaButtonText,
}: RemortgageCalculatorOptions) => {
  const [submitted, setSubmitted] = useState<boolean>(false)
  const [calculatorState, setCalculatorState] = useState<RemortgageCalculatorState>({
    propertyValue: 0,
    mortgageDebt: 0,
    yearsLeft: 0,
    monthsLeft: 0,
    currentInterestRate: 0,
    newInterestRate: 0,
    typeOfMortgage: null,
  })

  const results = useMemo<RemortgageCalculatorResults>(
    () =>
      submitted
        ? calculateRemortgage({
            mortgageBalance: calculatorState.mortgageDebt,
            currentRate: calculatorState.currentInterestRate,
            newRate: calculatorState.newInterestRate,
            years: calculatorState.yearsLeft,
            months: calculatorState.monthsLeft,
            typeOfMortgage: calculatorState.typeOfMortgage || TypeOfMortgage.Repayment,
          })
        : {
            deposit: 0,
            loanValue: 0,
            currentMonthlyPayment: 0,
            newMonthlyPayment: 0,
          },
    [calculatorState, submitted],
  )

  const updateState =
    (field: keyof RemortgageCalculatorState) => (e: ChangeEvent<HTMLInputElement>) =>
      setCalculatorState((prev) => ({ ...prev, [field]: e.target.value }))

  return (
    <div className='RemortgageCalculator BaseCalculator'>
      <script
        type='text/javascript'
        src='//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
        async
      />
      <div className='gauge-card main-content-container'>
        <div className='content-container'>
          <div className='left-side'>
            <h1>{title}</h1>
            <p>{subtitle}</p>
          </div>
          <div className='inner-card'>
            {!submitted ? (
              <CalculatorForm
                onSubmit={() => setSubmitted(true)}
                updateState={updateState}
                calculatorState={calculatorState}
              />
            ) : (
              <ResultsPage
                ctaButtonText={ctaButtonText}
                onRecalculate={() => setSubmitted(false)}
                results={results}
                deposit={calculatorState.propertyValue - calculatorState.mortgageDebt}
                loanValue={calculatorState.mortgageDebt}
                ltv={ltv(
                  calculatorState.propertyValue - calculatorState.mortgageDebt,
                  calculatorState.propertyValue,
                )}
              />
            )}
            <div className='logos'>
              <div className='mojo-logo'>
                <Icon glyph='powered-by-mojo' ariaLabel={'In partnership with Mojo'} />
              </div>
              <div className='trustpilot'>
                <Trustpilot theme='light' />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
